/**
 * Report Process Handlers
 *
 * All report-related query and mutation handlers
 */

import { handleRequest } from './config'
import { mockData } from '../mocks'
import { ApiResponse } from '../types'
import { loadCloudFunctionsHandler } from '../handlers'
import type { Report } from 'pure-js/types/Report'
import { ReportStatus } from 'pure-js/enums/ReportStatus'
import type { State } from '../../hooks/useAppState'

// ========================================
// QUERY HANDLERS
// ========================================

/**
 * Get all reports with optional filters
 */
export const getReports = async (
  filters?: Record<string, string | number | boolean>,
  state?: State
): Promise<ApiResponse<Report[]>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report[]>> => Promise.resolve({ data: mockData.reports, success: true }),
    async (): Promise<ApiResponse<Report[]>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      // Use provided state or empty state (state is prefixed with _ in cloud function, so not actually used)
      const stateToUse = state || ({ user: { id: '' } } as State)
      return await cloudFunctions.getReports(filters, stateToUse)
    }
  )
}

/**
 * Get a single report by ID
 */
export const getReport = async (id: string, state?: State): Promise<ApiResponse<Report>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report>> => {
      const report = mockData.reports.find((r) => r.id === id)
      if (!report) {
        return {
          data: {} as Report,
          success: false,
          error: 'Report not found'
        }
      }
      return {
        data: report,
        success: true,
        error: undefined
      }
    },
    async (): Promise<ApiResponse<Report>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || ({ user: { id: '' } } as State)
      return await cloudFunctions.getReport(id, stateToUse)
    }
  )
}

export type GetReportGenerationStatusResponse = {
  success: boolean
  reportId?: string
  status?: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | null
  error?: string
}

/**
 * Get report generation task status (for polling until completed; used by report-ready notification).
 */
export const getReportGenerationStatus = async (
  reportId: string,
  state?: State
): Promise<GetReportGenerationStatusResponse> => {
  return handleRequest<GetReportGenerationStatusResponse>(
    async () => ({ success: true, reportId, status: null }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || ({ user: { id: '' } } as State)
      return await cloudFunctions.getReportGenerationStatus(reportId, stateToUse)
    }
  )
}

/**
 * Get all reports for a specific session
 */
export const getReportsBySession = async (sessionId: string): Promise<ApiResponse<Report[]>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: mockData.reports.filter((r) => r.sessionIds.includes(sessionId)),
        success: true
      }),
    async () => {
      // TODO: Implement cloud function
      return { data: [], success: true }
    }
  )
}

/**
 * Get all reports for a specific team
 */
export const getReportsByTeam = async (teamId: string): Promise<ApiResponse<Report[]>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: mockData.reports.filter((r) => r.teamIds.includes(teamId)),
        success: true
      }),
    async () => {
      // TODO: Implement cloud function
      return { data: [], success: true }
    }
  )
}

/**
 * Get all reports for a specific user
 */
export const getReportsByUser = async (userId: string): Promise<ApiResponse<Report[]>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: mockData.reports.filter((r) => r.userId === userId),
        success: true
      }),
    async () => {
      // TODO: Implement cloud function
      return { data: [], success: true }
    }
  )
}

/**
 * Get all generated reports
 */
export const getGeneratedReports = async (): Promise<ApiResponse<Report[]>> => {
  return handleRequest(
    () => Promise.resolve({ data: mockData.reports, success: true }),
    async () => {
      // TODO: Implement cloud function
      return { data: [], success: true }
    }
  )
}

// ========================================
// MUTATION HANDLERS
// ========================================

/**
 * Create a new report
 */
export const createReport = async (reportData: Partial<Report>): Promise<ApiResponse<Report>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report>> =>
      Promise.resolve({
        data: {
          id: 'new-report-id',
          status: ReportStatus.DRAFT,
          userId: '',
          sprintIds: [],
          sessionIds: [],
          teamIds: [],
          reportData: {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...reportData
        } as Report,
        success: true
      }),
    async (): Promise<ApiResponse<Report>> => {
      // TODO: Implement cloud function
      return { data: {} as Report, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Update an existing report
 */
export const updateReport = async (
  id: string,
  reportData: Partial<Report>,
  state?: State
): Promise<ApiResponse<Report>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report>> =>
      Promise.resolve({
        data: { ...reportData, id, updatedAt: new Date().toISOString() } as Report,
        success: true
      }),
    async (): Promise<ApiResponse<Report>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || ({ user: { id: '' } } as State)
      return await cloudFunctions.updateReport(id, reportData, stateToUse)
    }
  )
}

/**
 * Finalize a report (change status from draft to finalized)
 */
export const finalizeReport = async (id: string, state?: State): Promise<ApiResponse<Report>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report>> =>
      Promise.resolve({
        data: {
          id,
          status: ReportStatus.FINALIZED,
          updatedAt: new Date().toISOString(),
          finalizedAt: new Date().toISOString(),
          title: '',
          userId: '',
          sprintIds: [],
          sessionIds: [],
          teamIds: [],
          reportData: {},
          createdAt: new Date().toISOString()
        } as Report,
        success: true
      }),
    async (): Promise<ApiResponse<Report>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || ({ user: { id: '' } } as State)
      return await cloudFunctions.finalizeReport(id, stateToUse)
    }
  )
}

/**
 * Delete a report
 */
export const deleteReport = async (id: string, state?: State): Promise<ApiResponse<void>> => {
  return handleRequest(
    () => Promise.resolve({ data: undefined, success: true } as ApiResponse<void>),
    async (): Promise<ApiResponse<void>> => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = state || ({ user: { id: '' } } as State)
      return await cloudFunctions.deleteReport(id, stateToUse)
    }
  )
}

/**
 * Generate a new report for a session
 */
export const generateReport = async (sessionId: string): Promise<ApiResponse<Report>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report>> =>
      Promise.resolve({
        data: {
          id: 'generated-report-id',
          title: 'Generated Report',
          status: ReportStatus.DRAFT,
          userId: '',
          sprintIds: [],
          sessionIds: [sessionId],
          teamIds: [],
          reportData: {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as Report,
        success: true
      }),
    async (): Promise<ApiResponse<Report>> => {
      // TODO: Implement cloud function
      return { data: {} as Report, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Generate report data for report flow (creates/updates a draft Report with datapoints)
 */
export const generateReportData = async (
  request: { sessions: { id: string; teamId: string }[]; reportId?: string; title?: string; sprintIds?: string[] },
  _state?: State
): Promise<ApiResponse<Report>> => {
  return handleRequest(
    async (): Promise<ApiResponse<Report>> =>
      Promise.resolve({
        data: {
          id: request.reportId || 'generated-report-id',
          title: request.title || 'Generated Report',
          status: ReportStatus.DRAFT,
          userId: '',
          sprintIds: request.sprintIds || [],
          sessionIds: request.sessions.map((s) => s.id),
          teamIds: Array.from(new Set(request.sessions.map((s) => s.teamId))),
          reportData: {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as Report,
        success: true
      }),
    async (): Promise<ApiResponse<Report>> => {
      // Use cloud function handler
      const cloudFunctions = await loadCloudFunctionsHandler()
      const stateToUse = _state || ({ user: { id: '' } } as State)
      const result = await cloudFunctions.generateReportData(request, stateToUse)
      return {
        data: result.data || ({} as Report),
        success: result.success,
        error: result.error
      }
    }
  )
}
